import axios from "axios";
import { isAuthenticated } from "../utils/auth";

const LOGIN_PATH = "/api/auth/login";
const REFRESH_PATH = "/api/auth/refresh-token";
const RESET_PASSWORD_PATH = "/api/auth/reset-password";
const FORGOT_PASSWORD_PATH = "/api/auth/forgot-password";
const SET_PASSWORD_PATH = "/api/auth/set-password";
const LOGOUT_PATH = "/api/auth/logout";

const AUTH_SKIP_PATHS = [
  LOGIN_PATH,
  REFRESH_PATH,
  RESET_PASSWORD_PATH,
  FORGOT_PASSWORD_PATH,
  SET_PASSWORD_PATH,
  LOGOUT_PATH,
];

const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return undefined;
  }

  return process.env.REACT_APP_API_BASE_URL || undefined;
};

const getRequestUrl = (config) => {
  if (!config) return "";

  try {
    return axios.getUri(config);
  } catch {
    return `${config.baseURL || ""}${config.url || ""}`;
  }
};

const matchesAuthPath = (url, path) => url.includes(path);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  failedQueue = [];
};

const clearSession = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("loginResponse");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("loginResponse");
};

const isLoginPage = () =>
  window.location.pathname.endsWith("/login");

const redirectToLogin = () => {
  if (isLoginPage()) return;

  clearSession();
  const loginUrl = `${window.location.origin}/login`;
  if (window.location.href !== loginUrl) {
    window.location.href = loginUrl;
  }
};

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = getRequestUrl(originalRequest);
    const isAuthPath = AUTH_SKIP_PATHS.some((path) =>
      matchesAuthPath(requestUrl, path)
    );

    if (status !== 401 || !originalRequest || originalRequest._retry || isAuthPath) {
      if (status === 401 && matchesAuthPath(requestUrl, REFRESH_PATH)) {
        redirectToLogin();
      }
      return Promise.reject(error);
    }

    if (!isAuthenticated()) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => axiosInstance(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await axiosInstance.post(REFRESH_PATH, {});
      processQueue(null);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      try {
        await axiosInstance.post(LOGOUT_PATH, {});
      } catch {
        // Ignore logout errors while clearing an expired session.
      }
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
